import { Link, useNavigate, useParams } from "@tanstack/react-router";

import {
  useArchiveWaterSource,
  useWaterSource,
} from "@/lib/api/water-sources";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { ErrorState } from "@/components/error-state";
import { AuditLogViewer } from "@/components/audit-log-viewer";

export function WaterSourceDetailPage() {
  const { waterSourceId } = useParams({
    from: "/chemistry-profile/water-sources/$waterSourceId",
  });
  const navigate = useNavigate();
  const q = useWaterSource(waterSourceId);
  const archive = useArchiveWaterSource(waterSourceId);

  if (q.isLoading)
    return <p className="text-sm text-muted-foreground">Loading…</p>;
  if (q.error) return <ErrorState error={q.error} retry={q.refetch} />;
  if (!q.data) return null;
  const w = q.data;

  function onArchive() {
    const reason =
      window.prompt("Reason for archiving this water source?") ||
      "user archive";
    archive.mutate(reason, {
      onSuccess: () => navigate({ to: "/chemistry-profile" }),
    });
  }

  return (
    <div className="space-y-6">
      <Link
        to="/chemistry-profile"
        className="text-sm text-muted-foreground hover:text-foreground"
      >
        ← Water sources
      </Link>

      <header className="flex items-center justify-between gap-4">
        <div className="space-y-1">
          <h1 className="text-2xl font-semibold tracking-tight">{w.name}</h1>
          <p className="text-sm text-muted-foreground">
            Water source{w.archived_at && " · archived"}
          </p>
        </div>
        {!w.archived_at && (
          <div className="flex gap-2">
            <Link
              to="/chemistry-profile/water-sources/$waterSourceId/edit"
              params={{ waterSourceId: w.id }}
            >
              <Button variant="outline">Edit</Button>
            </Link>
            <Button
              variant="ghost"
              onClick={onArchive}
              disabled={archive.isPending}
            >
              {archive.isPending ? "Archiving…" : "Archive"}
            </Button>
          </div>
        )}
      </header>

      <section className="grid gap-4 md:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Baseline
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-1 text-sm">
            <Row label="pH" value={w.baseline_ph} />
            <Row label="EC (mS/cm)" value={w.baseline_ec} />
            <Row label="Alkalinity estimate" value={w.alkalinity_estimate} />
          </CardContent>
        </Card>
        <Card>
          <CardHeader>
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Notes
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="whitespace-pre-wrap text-sm text-muted-foreground">
              {w.notes || "—"}
            </p>
          </CardContent>
        </Card>
      </section>

      <section className="space-y-2">
        <h2 className="text-sm font-medium uppercase tracking-wide text-muted-foreground">
          Audit trail
        </h2>
        <AuditLogViewer entityType="water_source" entityID={w.id} />
      </section>
    </div>
  );
}

function Row({ label, value }: { label: string; value: number | undefined }) {
  return (
    <div className="flex justify-between gap-4">
      <span className="text-muted-foreground">{label}</span>
      <span className="tabular-nums">{value ?? "—"}</span>
    </div>
  );
}
